import * as React from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { ArrowLeft } from "lucide-react"
import { useSWRConfig } from "swr"
import { SMITHERS_CLIENTS_KEY, useCreateSmithersClient } from "@/hooks/use-smithers-clients"
import { useToast } from "@/hooks/use-toast"
import type { SmithersClientConfig } from "@/types/smithers-client"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { DashboardHeader } from "@/components/shared/dashboard-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { getSmithersClientListHistoryState } from "@/lib/smithers-client-navigation"

export function SmithersClientAddPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [origin] = React.useState(() => getSmithersClientListHistoryState(location.state))
  const returnToClients = () => navigate("/smithers/clients", origin ? { replace: true, state: { smithersClientList: origin } } : undefined)
  const { mutate } = useSWRConfig()
  const { toast } = useToast()
  const createClient = useCreateSmithersClient()
  const submitting = React.useRef(false)
  const [submitError, setSubmitError] = React.useState<string>()
  const [clientId, setClientId] = React.useState("")
  const [touched, setTouched] = React.useState(false)
  const firstInputRef = React.useRef<HTMLInputElement>(null)
  const normalizedId = clientId.trim()
  const validationError = !normalizedId ? "Client ID is required" : Array.from(normalizedId).length > 100 ? "Client ID cannot be longer than 100 characters" : undefined
  const error = touched ? validationError : undefined

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (validationError || submitting.current) return
    submitting.current = true
    setSubmitError(undefined)
    try {
      const client = await createClient.trigger({ clientId: normalizedId })
      await mutate<SmithersClientConfig[]>(SMITHERS_CLIENTS_KEY, (current = []) => [...current.filter((item) => item.id !== client.id), client].sort((a, b) => a.clientId.localeCompare(b.clientId)), { revalidate: false })
      toast({ variant: "success", title: "Client created successfully", description: `Client ${client.clientId} has been added.` })
      returnToClients()
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to create client. Please try again."
      setSubmitError(message)
      toast({ variant: "error", title: "Failed to create client", description: message })
    } finally {
      submitting.current = false
    }
  }

  React.useEffect(() => {
    firstInputRef.current?.focus()
  }, [])

  return (
    <>
      <DashboardHeader />
      <main className="container mx-auto flex flex-col gap-6 px-4 py-6">
        <div>
          <Button type="button" variant="ghost" size="sm" disabled={createClient.isMutating} onClick={returnToClients} className="-ml-2 mb-4 text-muted-foreground"><ArrowLeft data-icon="inline-start" />Back to Clients</Button>
          <h1 className="text-2xl font-semibold text-foreground">Add Smithers Client</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Clients group Smithers devices. Configuration is managed at the global and device levels.
        </p>
        <form className="w-full max-w-2xl" onSubmit={handleSubmit}>
          <Card>
            <CardHeader className="border-b border-border bg-muted/40 px-5 py-4">
              <CardTitle className="text-base">Client Identification</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 p-6">
              {submitError && (
                <Alert variant="destructive">
                  <AlertTitle>Couldn’t create client</AlertTitle>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
              <div className="flex flex-col gap-2" data-invalid={error ? true : undefined}>
                <Label htmlFor="smithers-client-id">Client ID</Label>
                <Input
                  ref={firstInputRef}
                  id="smithers-client-id"
                  name="clientId"
                  value={clientId}
                  onChange={(event) => { setClientId(event.target.value); setSubmitError(undefined) }}
                  onBlur={() => setTouched(true)}
                  placeholder="e.g., dodgers"
                  required
                  pattern={".*\\S.*"}
                  disabled={createClient.isMutating}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "smithers-client-id-help smithers-client-id-error" : "smithers-client-id-help"}
                />
                <p id="smithers-client-id-help" className="text-sm text-muted-foreground">
                  Enter the client identity used to group and target devices.
                </p>
                <div className="min-h-10">{error && (
                  <p id="smithers-client-id-error" className="text-sm text-destructive" role="alert">
                    {error}
                  </p>
                )}</div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
              <Button type="button" variant="outline" disabled={(!clientId && !touched && !submitError) || createClient.isMutating} onClick={() => { setClientId(""); setTouched(false); setSubmitError(undefined) }}>Discard changes</Button>
              <Button type="submit" disabled={Boolean(validationError) || createClient.isMutating}>
                {createClient.isMutating ? "Creating…" : "Create Client"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </main>
    </>
  )
}
